import { defineStore, storeToRefs } from 'pinia'
import { reactive, ref } from 'vue'
import { Session, SessionQuestion } from '@/assets/types'
import { handleSubscription, newSessionQuestion } from './helper'
import { useQuestionnairesStore } from './questionnaires'
import { usePlayerStore } from './player'
import { auth, getToken } from '@/assets/utils'
import router from '@/router/router'

export const useSessionsStore = defineStore('sessions', () => {

    const popups = ref({});

    const sessions = reactive({
        current: [] as Session[],

        async init() {
            await this.fetch()
        },

        async fetch() {
            let data = []

            const url = new URL(
                `${import.meta.env.VITE_API_ENDPOINT}/sessions`,
                location.href
            )

            url.searchParams.set("order", 'desc')

            const res = await auth.get(
                url.toString(),
                getToken()
            )

            if (res.status == 200) {
                data = res.data ?? []
            }

            this.current = data.map(sessionFromAPI)
            return this.current as Session[]
        },

        async fetchOne(id: string) {
            const url = new URL(
                `${import.meta.env.VITE_API_ENDPOINT}/session/${id}`,
                location.href
            )

            const res = await auth.get(
                url.toString(),
                getToken()
            )

            let data = null

            if (res.status === 200) {
                data = sessionFromAPI(res.data)
            }

            return data
        },

        async fetchOneByPin(pin: string) {
            const url = new URL(
                `${import.meta.env.VITE_API_ENDPOINT}/session`,
                location.href
            )

            url.searchParams.set("pinCode", pin)

            const res = await auth.get(
                url.toString(),
                getToken()
            )

            let data = null

            if (res.status === 200) {
                data = sessionFromAPI(res.data ?? {})
            }

            return data
        },

        async create(s: Session) {
            const url = new URL(
                `${import.meta.env.VITE_API_ENDPOINT}/sessions`,
                location.href
            )

            const res = await auth.post(
                url.toString(),
                sessionToAPI(s),
                getToken()
            )

            if (res.status == 200) {
                return res.data
            } else {
                return null
            }
        },

        async edit(session: Session) {
            const url = new URL(
                `${import.meta.env.VITE_API_ENDPOINT}/session/${session.id}`,
                location.href
            )

            const res = await auth.put(
                url.toString(),
                sessionToAPI(session),
                getToken()
            )

            if (res.status == 200) {
                return res.data
            } else {
                return null
            }
        },

        async delete(id: string) {
            const url = new URL(
                `${import.meta.env.VITE_API_ENDPOINT}/session/${id}`,
                location.href
            )

            const res = await auth.delete(
                url.toString(),
                getToken()
            )

            if (res.status == 200) {
                return res.data
            } else {
                return null
            }
        },

        sub(id?: string) {
            if (!id) {
                return handleSubscription(
                    'sessions',
                    {
                        create: (doc) => {
                            const session = sessionFromAPI(doc)
                            this.current.unshift(session)
                        },
                        update: (doc) => {
                            const session = sessionFromAPI(doc)
                            const index = this.current.findIndex((q: Session) => q.id === session.id)
                            this.current.splice(index, 1, session)

                            // handle currentSession
                            if (session.id === currentSession.value?.id) {
                                currentSession.value = session
                            }
                        },
                        delete: (doc) => {
                            const index = this.current.findIndex((q: Session) => q.id === doc.id)
                            this.current.splice(index, 1)
                            // handle currentSession
                            if (doc.id === currentSession.value?.id) {
                                currentSession.value = null
                                //redirect to index page
                                router.replace("/")
                            }
                            // handle popup
                            const pop = popups.value[doc.id] as WindowProxy
                            if (pop) {
                                pop.close()
                            }
                        },
                    }
                )
            } else {
                return handleSubscription(
                    `sessions`,
                    {
                        create: () => { },
                        update: (doc) => {
                            if (doc.id !== id) return;
                            const session = sessionFromAPI(doc)
                            const index = this.current.findIndex((q: Session) => q.id === session.id)
                            this.current.splice(index, 1, session)
                            // handle currentSession
                            if (session.id === currentSession.value?.id) {
                                currentSession.value = session
                            }
                        },
                        delete: (doc) => {
                            if (doc.id !== id) return;
                            const index = this.current.findIndex((q: Session) => q.id === doc.id)
                            this.current.splice(index, 1)
                            // handle currentSession
                            if (doc.id === currentSession.value?.id) {
                                currentSession.value = null
                                //redirect to index page
                                router.replace("/")
                            }
                        },
                    }
                )
            }
        },

        async getSessionResults(id: string) {

            let data = []

            const url = new URL(
                `${import.meta.env.VITE_API_ENDPOINT}/players`,
                location.href
            )

            url.searchParams.set("order", 'desc')
            url.searchParams.set("order_by", 'score')
            url.searchParams.set("limit", encodeURIComponent(10))
            url.searchParams.set("sessionId", encodeURIComponent(id))

            const res = await auth.get(
                url.toString(),
                getToken()
            )

            if (res.status == 200) {
                data = res.data ?? []
            }

            return data
        },

        async getSessionQuestionResults(id: string, questionId: number, limit = 10) {
            let data = []

            const url = new URL(
                `${import.meta.env.VITE_API_ENDPOINT}/answers`,
                location.href
            )

            url.searchParams.set("order", 'desc')
            url.searchParams.set("order_by", "earnedPoints")
            if (limit > 0) {
                url.searchParams.set("limit", encodeURIComponent(limit))
            }
            url.searchParams.set("sessionId", encodeURIComponent(id))
            url.searchParams.set("questionIndex", encodeURIComponent(questionId))

            const res = await auth.get(
                url.toString(),
                getToken()
            )

            if (res.status == 200) {
                data = res.data ?? []
            }

            return data
        }
    })

    const currentSession = ref<Session | null>(null)

    async function setCurrentSessionByPin(pin: string) {
        if (!pin) {
            currentSession.value = null
            return null
        }

        return await sessions.fetchOneByPin(pin).then(session => {
            if (session) {
                currentSession.value = session
                sessions.current.push(session)
            } else {
                currentSession.value = null
            }
            return currentSession.value
        })
    }

    async function startSession(session: Session) {
        const { questionnaires } = storeToRefs(useQuestionnairesStore())
        const playersStore = usePlayerStore()

        const questionnaire = await questionnaires.value.fetchOne(session.questionnaireId)
        if (!questionnaire || !questionnaire.questions || questionnaire.questions.length === 0) {
            console.error('Cannot start session: Questionnaire not found or empty')
            return
        }

        // save players count
        const { total: playersCount } = await playersStore.getPlayers(session.id)
        // Get the first question from the linked questionnaire
        const firstQuestionData = questionnaire.questions[0]
        // Create the SessionQuestion object for currentQuestion
        const initialCurrentQuestion: SessionQuestion = newSessionQuestion(0, firstQuestionData)
        // Update the session state and currentQuestion in the store
        sessions.edit({
            ...session,
            state: 'play',
            currentQuestion: initialCurrentQuestion,
            playersCount: playersCount,
            totalQuestions: questionnaire.questions.length,
        })
    }

    async function continueSession(session: Session) {
        const { questionnaires } = storeToRefs(useQuestionnairesStore())

        // Get the next question from the linked questionnaire or close current question
        if (!session.currentQuestion) {
            return
        }

        const currentQuestion = session.currentQuestion as SessionQuestion
        const questionnaire = await questionnaires.value.fetchOne(session.questionnaireId)

        // If the currentQuestion is not yet answered, close it
        if (currentQuestion.correctAnswer === null) {
            // get the answer
            currentQuestion.correctAnswer = questionnaire.questions[
                currentQuestion.index
            ].answers.findIndex((a: any) => a.correct)

            // Update the currentQuestion in the store
            sessions.edit({ ...session, currentQuestion })
            return
        }

        // Get the next question from the linked questionnaire
        const nextQuestionData = questionnaire.questions.at(currentQuestion.index + 1)
        if (!nextQuestionData) {
            sessions.edit({ ...session, state: 'stop', currentQuestion: null })
            return
        }
        const nextQuestion: SessionQuestion = newSessionQuestion(
            currentQuestion.index + 1,
            nextQuestionData,
        )

        // Update the session state and currentQuestion in the store
        sessions.edit({ ...session, state: 'play', currentQuestion: nextQuestion })
    }


    return {
        popups,
        sessions,
        currentSession,
        setCurrentSessionByPin,
        startSession,
        continueSession,
    }
})

function sessionFromAPI(doc: any) {
    return {
        ...doc,
        currentQuestion: doc.currentQuestion as SessionQuestion,
    } as Session
}

function sessionToAPI(session: Session) {
    const sess = { ...session }
    sess.currentQuestion = sess.currentQuestion
    return sess
}